import lunr from 'lunr';
import cx from 'classnames';
import { useCombobox } from 'downshift';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import memoizeOne from 'memoize-one';
import { useHistory } from 'react-router';

import { isDossierArchived } from '../../../domain';

import sloth from '../../../assets/sloth.svg';
import { Archive } from '../../../design-system/Icons/Archive';
import { Close } from '../../../design-system/Icons/Close';
import { Search } from '../../../design-system/Icons/Search';
import { Loader } from '../../../design-system/Loader';
import { DossierId, DossierOverview, NormalizedDossier } from '../../../resources/dossier/types';
import { searchInIndex } from '../../../resources/dossier-overview';

import {
    useGetArchivedDossierOverviews,
    useGetDossierOverviews,
} from '../../../resources/dossier-overview/hooks';

import s from './Searchbar.css';

function SearchItem({ id, project: { requesterName }, archiveDate }: DossierOverview): JSX.Element {
    return (
        <div data-testid="resultItem" className={s.result}>
            {archiveDate && <Archive color="var(--ink)" />}
            <p className="bold" data-testid="requestName">
                {requesterName}
            </p>
            <p data-testid="dossierId" className={s.dossierId}>
                {`#${id}`}
            </p>
        </div>
    );
}

/**
 * Because we are memoizing a function which argument is an object, we cannot make a shallow compare as it will always be false since the reference of the object will always be different.
 * We need to make a custom comparator to compare what should trigger a new compute of the indexed list, here length should be enough
 */
const customComparator = ([first]: NormalizedDossier[], [second]: NormalizedDossier[]) => {
    return Object.values(first).length === Object.values(second).length;
};

export const getDossierOverviewSearchList = memoizeOne((dossiers: NormalizedDossier) => {
    const list = Object.values(dossiers)
        .filter((dossier) => !isDossierArchived(dossier))
        .map((dossier) => ({
            id: dossier.id,
            name: dossier.project.requesterName
                .normalize('NFD')
                // This line removes accents
                .replace(/[\u0300-\u036f]/g, ''),
            phone: dossier.project.requesterPhone,
        }));

    return lunr(function (builder) {
        builder.ref('id');
        builder.field('id');
        builder.field('name');
        builder.field('phone');
        builder.pipeline.remove(lunr.stemmer);
        builder.pipeline.remove(lunr.stopWordFilter);
        builder.searchPipeline.remove(lunr.stemmer);
        builder.searchPipeline.remove(lunr.stopWordFilter);
        list.forEach((dossier) => {
            builder.add(dossier);
        });
    });
}, customComparator);

export function Searchbar(): JSX.Element {
    const history = useHistory();
    const { formatMessage } = useIntl();

    const [query, setQuery] = useState('');
    const [shouldSearchInArchivedDossiers, setShouldSearchInArchivedDossiers] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    let input: HTMLInputElement | null = null;

    const { data: dossierOverviews } = useGetDossierOverviews();
    const { data: archivedDossierOverviews, status: archivedDossierOverviewsStatus } =
        useGetArchivedDossierOverviews(query, {
            enabled: shouldSearchInArchivedDossiers,
        });

    const allDossiers = {
        ...dossierOverviews,
        ...(shouldSearchInArchivedDossiers ? archivedDossierOverviews : {}),
    };

    const archivedDossierIds = shouldSearchInArchivedDossiers
        ? Object.keys(archivedDossierOverviews || {})
        : [];

    function handleSearch(search: string): void {
        if (!search) {
            return resetSearch();
        }
        if (shouldSearchInArchivedDossiers) {
            setShouldSearchInArchivedDossiers(false);
        }

        setQuery(search);
    }

    function search(): string[] {
        if (!query || !allDossiers) {
            return [];
        }
        const dossierOverviewSearchList = getDossierOverviewSearchList(allDossiers);
        return searchInIndex(query.toLowerCase(), dossierOverviewSearchList);
    }

    function hasNoResult(isOpen: boolean, results: string[]): boolean {
        return isOpen && query.length >= 2 && results.length === 0;
    }

    function onBlur(): void {
        input?.blur();
        setIsFocused(false);
    }

    function onSelect(dossierId: DossierId | null): void {
        if (!dossierId) {
            return;
        }
        onBlur();
        resetSearch();
        history.push(`/dossier/${dossierId}`);
    }

    const searchResults = search().concat(archivedDossierIds);

    const {
        isOpen,
        getInputProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        inputValue,
        reset,
    } = useCombobox({
        items: searchResults,
        onInputValueChange: ({ inputValue }) => {
            const value = inputValue || '';
            setQuery(value);
            handleSearch(value);
        },
        onSelectedItemChange: ({ selectedItem }) => {
            onSelect(selectedItem || null);
        },
        itemToString: (item) => {
            return item ? allDossiers?.[item]?.project?.requesterName || item : '';
        },
    });

    function resetSearch(): void {
        reset();
        setQuery('');
        setShouldSearchInArchivedDossiers(false);
    }

    return (
        <div className={s.container}>
            {!isFocused && !isOpen && (
                <Search
                    color="var(--ink-light)"
                    className={s.searchIcon}
                    width="20px"
                    height="20px"
                />
            )}
            <input
                data-testid="input"
                {...getInputProps({
                    ref: (ref: HTMLInputElement | null) => (input = ref),
                })}
                className={cx(s.input, {
                    [s.expanded]: isOpen || isFocused,
                })}
                placeholder={formatMessage({
                    id: 'dossier.search_dossier',
                })}
                onFocus={() => setIsFocused(true)}
                onBlur={onBlur}
            />
            <ul {...getMenuProps()} className={cx(s.list, s.roundedBottom)}>
                {isOpen &&
                    allDossiers &&
                    searchResults.map((id, index) => (
                        <li
                            key={id}
                            data-testid="listItem"
                            className={cx(s.item, {
                                [s.highlighted]: highlightedIndex === index,
                                [s.roundedBottom]: index === searchResults.length,
                            })}
                            {...getItemProps({
                                index,
                                item: id,
                            })}
                        >
                            <SearchItem {...allDossiers[id]} />
                        </li>
                    ))}
                {hasNoResult(isOpen, searchResults) && (
                    <li data-testid="noResult" className={s.noresult}>
                        <p>
                            <FormattedMessage
                                id="dossier.search_no_result"
                                values={{
                                    query: query,
                                }}
                            />
                        </p>
                        <img src={sloth} alt="sloth image" />
                    </li>
                )}
                {isFocused && query.length < 2 && (
                    <li data-testid="tip" className={s.noresult}>
                        <p>
                            <FormattedMessage id="dossier.search_tip" />
                        </p>
                    </li>
                )}
                {isOpen &&
                    (!shouldSearchInArchivedDossiers ||
                        archivedDossierOverviewsStatus === 'error') &&
                    query.length >= 2 && (
                        <li>
                            <button
                                type="button"
                                className={s.archiveSearchButton}
                                onClick={() => {
                                    setShouldSearchInArchivedDossiers(true);
                                }}
                                data-testid="search-archive"
                            >
                                <Archive color="var(--lime)" />
                                <FormattedMessage id="dossier.search_archive" />
                            </button>
                        </li>
                    )}
                {isOpen &&
                    shouldSearchInArchivedDossiers &&
                    archivedDossierOverviewsStatus === 'pending' && (
                        <li className={s.loader}>
                            <Loader />
                        </li>
                    )}
            </ul>
            {isOpen && inputValue && (
                <button
                    type="button"
                    data-testid="clear"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        resetSearch();
                    }}
                    className={s.delete}
                >
                    <Close color="var(--white)" />
                </button>
            )}
        </div>
    );
}
